import { memo, useMemo, useState } from "react";
import { ArrowUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const FORMATTERS = {
  currency(value) {
    return Number(value ?? 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    });
  },

  number(value) {
    return Number(value ?? 0).toLocaleString("en-IN");
  },

  percent(value) {
    return `${Number(value ?? 0).toFixed(2)}%`;
  },

  text(value) {
    return value ?? "";
  },

  date(value) {
    if (!value) return "";

    return new Date(value).toLocaleDateString("en-IN");
  },
};

function DataTable({
  columns = [],
  data = [],
  emptyMessage = "No data available.",
}) {
  const [sort, setSort] = useState(null);

  const sortedData = useMemo(() => {
    if (!sort) return data;

    const rows = [...data];

    rows.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];

      if (typeof av === "number" && typeof bv === "number") {
        return sort.direction === "asc"
          ? av - bv
          : bv - av;
      }

      return sort.direction === "asc"
        ? String(av ?? "").localeCompare(String(bv ?? ""))
        : String(bv ?? "").localeCompare(String(av ?? ""));
    });

    return rows;
  }, [data, sort]);

  if (!data.length) {
    return (
      <div className="rounded-xl border border-dashed p-12 text-center text-muted-foreground">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border">
      <div className="max-h-[650px] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-20 bg-background">
            <TableRow>
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className={
                    column.align === "left"
                      ? "text-left"
                      : "text-right"
                  }
                >
                  {column.sortable ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 font-semibold"
                      onClick={() =>
                        setSort((prev) => ({
                          key: column.key,
                          direction:
                            prev?.key === column.key &&
                            prev.direction === "asc"
                              ? "desc"
                              : "asc",
                        }))
                      }
                    >
                      {column.label}

                      <ArrowUpDown className="ml-2 h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    column.label
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>

          <TableBody>
            {sortedData.map((row, index) => (
              <TableRow key={index}>
                {columns.map((column) => {
                  const formatter =
                    FORMATTERS[column.type] ??
                    FORMATTERS.text;

                  return (
                    <TableCell
                      key={column.key}
                      className={
                        column.align === "left"
                          ? "text-left"
                          : "text-right font-medium"
                      }
                    >
                      {formatter(row[column.key])}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default memo(DataTable);